<template>
	<JBox class="s-p-4">
		<!-- Text alignment -->
		<JBox class="s-border-b s-border-gray-300 s-pb-4">
			<JLabel class="s-text-sm s-text-gray-900 s-mb-2 s-font-bold">{{ $t("templates.textAlignment") }}</JLabel>
			<JFlex>
				<div
					class="s-p-2 s-rounded s-mr-2 s-cursor-pointer"
					@click="setTextAlignment('LEFT')"
					:class="textAlignment === 'LEFT' ? 's-bg-indigo-500 s-text-white' : 's-bg-gray-200'"
				>
					<svg
						width="20"
						height="20"
						fill="currentColor"
						aria-hidden="true"
						focusable="false"
						data-prefix="fas"
						data-icon="align-left"
						role="img"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 448 512"
					>
						<path
							fill="currentColor"
							d="M12.83 352h262.34A12.82 12.82 0 0 0 288 339.17v-38.34A12.82 12.82 0 0 0 275.17 288H12.83A12.82 12.82 0 0 0 0 300.83v38.34A12.82 12.82 0 0 0 12.83 352zm0-256h262.34A12.82 12.82 0 0 0 288 83.17V44.83A12.82 12.82 0 0 0 275.17 32H12.83A12.82 12.82 0 0 0 0 44.83v38.34A12.82 12.82 0 0 0 12.83 96zM432 160H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm0 256H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16z"
						></path>
					</svg>
				</div>
				<div
					@click="setTextAlignment('CENTER')"
					class="s-p-2 s-rounded s-mr-2 s-cursor-pointer"
					:class="textAlignment === 'CENTER' ? 's-bg-indigo-500 s-text-white' : 's-bg-gray-200'"
				>
					<svg
						width="20"
						height="20"
						fill="currentColor"
						aria-hidden="true"
						focusable="false"
						data-prefix="fas"
						data-icon="align-center"
						role="img"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 448 512"
					>
						<path
							fill="currentColor"
							d="M432 160H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm0 256H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM108.1 96h231.81A12.09 12.09 0 0 0 352 83.9V44.09A12.09 12.09 0 0 0 339.91 32H108.1A12.09 12.09 0 0 0 96 44.09V83.9A12.1 12.1 0 0 0 108.1 96zm231.81 256A12.09 12.09 0 0 0 352 339.9v-39.81A12.09 12.09 0 0 0 339.91 288H108.1A12.09 12.09 0 0 0 96 300.09v39.81a12.1 12.1 0 0 0 12.1 12.1z"
						></path>
					</svg>
				</div>
				<div
					@click="setTextAlignment('RIGHT')"
					class="s-p-2 s-rounded s-cursor-pointer"
					:class="textAlignment === 'RIGHT' ? 's-bg-indigo-500 s-text-white' : 's-bg-gray-200'"
				>
					<svg
						width="20"
						height="20"
						fill="currentColor"
						aria-hidden="true"
						focusable="false"
						data-prefix="fas"
						data-icon="align-right"
						role="img"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 448 512"
					>
						<path
							fill="currentColor"
							d="M16 224h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16zm416 192H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm3.17-384H172.83A12.82 12.82 0 0 0 160 44.83v38.34A12.82 12.82 0 0 0 172.83 96h262.34A12.82 12.82 0 0 0 448 83.17V44.83A12.82 12.82 0 0 0 435.17 32zm0 256H172.83A12.82 12.82 0 0 0 160 300.83v38.34A12.82 12.82 0 0 0 172.83 352h262.34A12.82 12.82 0 0 0 448 339.17v-38.34A12.82 12.82 0 0 0 435.17 288z"
						></path>
					</svg>
				</div>
			</JFlex>
		</JBox>

		<!-- Coordinates -->
		<JBox class="s-py-4 s-border-b s-border-gray-300">
			<!-- X and Y -->
			<JFlex class="s-mb-4">
				<JFlex class="s-items-center s-mr-4">
					<JLabel class="s-text-sm s-text-gray-900 s-mr-2 s-font-bold">{{ $t("templates.x") }}</JLabel>
					<JInputNumber v-model="point1.x" type="float" />
				</JFlex>
				<JFlex class="s-items-center">
					<JLabel class="s-text-sm s-text-gray-900 s-mr-2 s-font-bold">{{ $t("templates.y") }}</JLabel>
					<JInputNumber v-model="point1.y" type="float" />
				</JFlex>
			</JFlex>

			<!-- Width and Height -->
			<JFlex>
				<JFlex class="s-items-center s-mr-4">
					<JLabel class="s-text-sm s-text-gray-900 s-mr-2 s-font-bold">{{ $t("templates.w") }}</JLabel>
					<JInputNumber :value="width" @input="setWidth" type="float" />
				</JFlex>
				<JFlex class="s-items-center">
					<JLabel class="s-text-sm s-text-gray-900 s-mr-2 s-font-bold">{{ $t("templates.h") }}</JLabel>
					<JInputNumber :value="height" @input="setHeight" type="float" />
				</JFlex>
			</JFlex>
		</JBox>

		<!-- Font -->
		<JBox class="s-py-4 s-border-b s-border-gray-300">
			<JLabel class="s-text-sm s-text-gray-900 s-font-bold">{{ $t("templates.font") }}</JLabel>
			<JSelect class="s-mb-2" :options="fontList" v-model="font.familyName" />
			<JSelect class="s-mb-2" :options="fontStyles" v-model="font.fontStyle" />
			<JSelect :options="fontSizes" v-model="font.emSize" />
		</JBox>

		<!-- Liquid -->
		<JBox class="s-py-4">
			<JFlex class="s-justify-between s-items-center">
				<JLabel class="s-text-sm s-text-gray-900 s-font-bold">{{ $t("templates.liquid") }}</JLabel>
				<JButton variant="default-light-sm" class="s-mb-2" @click="toggleLiquidPicker">{{
					$t("templates.liquidPicker")
				}}</JButton>
			</JFlex>

			<JInputLongText v-model="liquidSource" />
			<JFlex class="s-mt-2 s-justify-end">
				<!-- <JButton variant="default-light" class="s-mr-2">{{ $t("templates.cancel") }}</JButton> -->
				<JButton variant="default-sm" @click="updatePdfDrawing">{{ $t("templates.save") }}</JButton>
			</JFlex>
		</JBox>

		<!-- LiquidPicker -->
		<CModal :isVisible="showLiquidPicker" @overlay:click="toggleLiquidPicker">
			<JBox class="s-bg-white s-p-2">
				<CLiquidPicker @data:selected="handleLiquidPicker" />
			</JBox>
		</CModal>
	</JBox>
</template>

<script>
import fontList from "../fontList.json"
import fontStyles from "../fontStyles.json"

import api from "@/api/api"
import { mutation } from "../../../api/queries.js"

const defaultFontSettings = {
	familyName: null,
	emSize: null,
	fontStyle: null,
}

const defaultPointSettings = {
	x: 0,
	y: 0,
}

export default {
	props: {
		templateId: {
			type: String,
		},
		drawings: {
			type: Array,
			default: () => [],
		},
		selectedDrawingId: {
			type: String,
			default: null,
		},
	},

	data() {
		return {
			liquidSource: null,
			textAlignment: null,
			verticalAlignment: null,
			font: {
				familyName: null,
				emSize: null,
				fontStyle: null,
			},
			point1: {
				x: null,
				y: null,
			},
			point2: {
				x: null,
				y: null,
			},
			width: null,
			height: null,

			showLiquidPicker: false,
		}
	},

	methods: {
		setTextAlignment(payload) {
			this.textAlignment = payload
		},
		setWidth(value) {
			this.point2.x = parseFloat(this.point1?.x) + parseFloat(value)
		},
		setHeight(value) {
			this.point2.y = parseFloat(this.point1?.y) + parseFloat(value)
		},
		async updatePdfDrawing() {
			this.$emit("drawing:isSaving", true)
			const input = {
				liquidSource: this.liquidSource,
				textAlignment: this.textAlignment,
				verticalAlignment: this.verticalAlignment,
				font: {
					familyName: this.font?.familyName,
					emSize: parseInt(this.font?.emSize),
					fontStyle: this.font?.fontStyle,
				},
				point1: {
					...this.point1,
				},
				point2: {
					...this.point2,
				},
			}
			const variables = {
				templateId: this.templateId,
				drawingId: this.selectedDrawingId,
				input: input,
			}
			const res = await api({ query: mutation.updatePdfDrawing, variables })
			this.$emit("drawing:isSaving", false)
			if (res?.data?.updatePdfDrawing?.status !== "success") {
				this.$emit("errorMessage", res?.errors?.[0] || res?.data?.updatePdfDrawing?.errors?.[0])
				this.$emit("drawing:error")
				return
			}

			this.$emit("drawing:updated", input)
			this.$store.dispatch("addToastMessage", { type: "success", content: "PDF drawing updated successfully" })
		},
		initialize() {
			this.liquidSource = this.selectedDrawing?.liquidSource
			this.textAlignment = this.selectedDrawing?.textAlignment
			this.font = this.selectedDrawing?.font ? this.selectedDrawing?.font : defaultFontSettings
			this.point1 = this.selectedDrawing?.point1 ? this.selectedDrawing?.point1 : defaultPointSettings
			this.point2 = this.selectedDrawing?.point2 ? this.selectedDrawing?.point2 : defaultPointSettings
			this.width = this.point2.x - this.point1.x
			this.height = this.point2.y - this.point1.y
		},
		toggleLiquidPicker() {
			this.showLiquidPicker = !this.showLiquidPicker
		},
		handleLiquidPicker(payload) {
			this.liquidSource = payload
			this.toggleLiquidPicker()
		},
		handleKeyDown(event) {
			if (event.ctrlKey && event.key === "s") {
				event.preventDefault()
				this.updatePdfDrawing()
			}
		},
	},

	computed: {
		selectedDrawing() {
			return this.drawings?.find(item => item.id === this.selectedDrawingId)
		},
		fontList() {
			return fontList.fonts.map(font => {
				return {
					value: font,
					name: font,
				}
			})
		},
		fontStyles() {
			return fontStyles.styles.map(font => {
				return {
					value: font,
					name: font,
				}
			})
		},
		fontSizes() {
			let output = []
			for (let i = 1; i <= 64; i++) {
				output.push({
					value: i,
					name: i,
				})
			}
			return output
		},
	},

	created() {
		window.addEventListener("keydown", this.handleKeyDown)
	},

	beforeDestroy() {
		window.removeEventListener("keydown", this.handleKeyDown)
	},

	watch: {
		selectedDrawingId: {
			handler() {
				this.initialize()
			},
			deep: true,
		},
		drawings: {
			handler() {
				this.initialize()
			},
			deep: true,
		},
	},
}
</script>
